import React from 'react';
import CheckboxInput from '../../../../../muiComponents/CheckboxInput';
import {
    ScraperAttribute,
    ScraperAttributeStatus,
    ScraperAttributeValueSource,
} from '../../../types/scrapedServicesTypes';
import { scraperAttributeStatusConfig } from '../../../utils/AIScraperPanelsUtils';
import useTranslate from '../../../../../customHooks/useTranslate';
import { useServiceScraperPanel } from '../../../contexts/ServiceScraperPanelContext';
import ScraperAttributeValueInput from './ScraperAttributeValueInput';
import ScraperStatusTag from './ScraperStatusTag';

interface ServiceAttributeRowProps {
    attribute: ScraperAttribute;
    categoryId: number;
    serviceTypeId: number;
    disabled?: boolean;
}

const ServiceAttributeRow = ({
    attribute,
    categoryId,
    serviceTypeId,
    disabled = false,
}: ServiceAttributeRowProps) => {
    const translate = useTranslate();
    const {
        setAttributeChecked,
        setAttributeValue,
        setAttributeSelectedSource,
    } = useServiceScraperPanel();
    const handleValueChange = (
        value: string,
        source?: ScraperAttributeValueSource
    ) => {
        setAttributeValue(
            categoryId,
            serviceTypeId,
            attribute.IdAttributeDefinition,
            value,
            source
        );
    };
    const handleSelectSource = (source: ScraperAttributeValueSource) => {
        if (attribute.SelectedValueSource !== source) {
            setAttributeSelectedSource(
                categoryId,
                serviceTypeId,
                attribute.IdAttributeDefinition,
                source
            );
        }
    };
    const isSuggestedSelected =
        attribute.SelectedValueSource === 'SuggestedValue';
    const fieldType = attribute.FieldType;
    const isValueDisabled = disabled || !attribute.Checked;
    return (
        <li
            key={attribute.IdAttributeDefinition}
            className='service-scraper__attribute'
        >
            <CheckboxInput
                label={attribute.AttributeDefinition}
                checked={attribute.Checked}
                disabled={disabled}
                onChange={(_, checked) =>
                    setAttributeChecked(
                        categoryId,
                        serviceTypeId,
                        attribute.IdAttributeDefinition,
                        checked
                    )
                }
            />
            <div className='flex gap-10'>
                {attribute.Status === ScraperAttributeStatus.CONFLICT ? (
                    <div className='flex-column gap-20 service-scraper__conflict'>
                        <label className='service-scraper__conflict-attribute-label'>
                            {translate('conflict_choose_value')}
                        </label>
                        <div className='flex gap-10 service-scraper__conflict-values'>
                            <ScraperAttributeValueInput
                                fieldType={fieldType}
                                label={'current_value'}
                                value={attribute.CurrentValue ?? ''}
                                disabled={isValueDisabled}
                                onChange={value =>
                                    handleValueChange(value, 'CurrentValue')
                                }
                                onFocus={() =>
                                    handleSelectSource('CurrentValue')
                                }
                                className={
                                    !isSuggestedSelected
                                        ? 'service-scraper__selected-value'
                                        : 'service-scraper__non-selected-value'
                                }
                            />
                            <ScraperAttributeValueInput
                                fieldType={fieldType}
                                label={'suggested_value'}
                                value={attribute.SuggestedValue}
                                disabled={isValueDisabled}
                                onChange={value =>
                                    handleValueChange(value, 'SuggestedValue')
                                }
                                onFocus={() =>
                                    handleSelectSource('SuggestedValue')
                                }
                                className={
                                    isSuggestedSelected
                                        ? 'service-scraper__selected-value'
                                        : 'service-scraper__non-selected-value'
                                }
                            />
                        </div>
                    </div>
                ) : (
                    <ScraperAttributeValueInput
                        fieldType={fieldType}
                        label={'value'}
                        value={attribute.Value}
                        disabled={isValueDisabled}
                        onChange={value => handleValueChange(value)}
                    />
                )}
            </div>
            <ScraperStatusTag
                text={attribute.Status}
                color={scraperAttributeStatusConfig[attribute.Status]}
                size='small'
            />
        </li>
    );
};

export default ServiceAttributeRow;
